<template>
    <modal-dialog
        id="new-project"
        :show.sync="shown"
        class="uk-modal-dialog-large"
    >
        <template v-slot:title>
            <span v-if="!!scope">New Multi-Scope Project</span>
            <span v-else>New <span v-if="internal">Overhead</span><span v-else>Prospective</span> Project</span>
        </template>

        <div class="uk-panel">
            <form class="uk-form" role="form" @submit.prevent.stop="create">
                <h4 :class="{'uk-text-required': !project.name}">
                    <open-icon v-if="!project.name" glyph="exclamation-triangle" />
                    <span v-if="!!scope">Scope Name</span>
                    <span v-else>Name</span>
                </h4>
                <form-input
                    v-if="!!scope"
                    id="new-project-nickname"
                    v-model="project.nickname"
                    name="nickname"
                    :error="has_error('nickname')"
                    :required="true"
                    :editable="true"
                    width="1-1"
                    placeholder="Scope name"
                    class="uk-width-1-1"
                />
                <form-input
                    v-else
                    id="new-project-name"
                    v-model="project.name"
                    name="name"
                    :error="has_error('name')"
                    :required="true"
                    :editable="true"
                    width="1-1"
                    placeholder="Project name"
                    class="uk-width-1-1"
                />

                <div class="uk-grid uk-grid-small uk-margin-top">
                    <div class="uk-width-1-3">
                        <h4>Main Contact</h4>
                    </div>
                    <div class="uk-width-1-3">
                        <base-button
                            v-if="!!comments.length"
                            flavor="link"
                            title="Client Notes"
                            size="small"
                            class="uk-button-inline-patch"
                            @click.prevent="displaying_comments = !displaying_comments"
                        >
                            (
                                <open-icon glyph="comments" />
                                Client Notes
                            )
                        </base-button>
                    </div>
                </div>
                <div class="uk-grid uk-grid-small">
                    <div class="uk-width-1-2">
                        <form-select-combo
                            id="new-project-contact-main-role"
                            name="role"
                            v-model="project.role"
                            empty-label="(Pick one)"
                            :error="has_error('role')"
                            :editable="true"
                            :required="false"
                        />
                    </div>
                    <div class="uk-width-1-2">
                        <firm-picker
                            id="new-project-contact-main-firm_id"
                            :value="project.firm_id"
                            placeholder="Firm"
                            :editable="true"
                            @pick="set_firm"
                        />
                    </div>
                </div>

                <div class="uk-grid uk-grid-small">
                    <div class="uk-width-1-2">
                        <div class="uk-form-controls-text uk-text-bold">Received by</div>
                        <form-select-user
                            id="received_by"
                            name="received_by"
                            v-model="project.received_by"
                            :editable="true"
                            :required="false"
                            class="uk-width-1-1"
                        />
                    </div>
                    <div class="uk-width-1-2">
                        <div class="uk-form-controls-text uk-text-bold">Received on</div>
                        <form-date-input
                            id="received_at"
                            v-model="project.received_at"
                            name="received_at"
                            :editable="true"
                            :required="false"
                        />
                    </div>
                </div>

                <div class="uk-modal-footer uk-text-center">
                    <div class="uk-grid">
                        <div class="uk-width-1-2">
                            <base-button
                                size="large"
                                width="1-1"
                                :disabled="creating"
                                @click.prevent="close"
                                class="uk-width-1-1"
                            >
                                Cancel
                            </base-button>
                        </div>
                        <div class="uk-width-1-2">
                            <base-button
                                type="submit"
                                size="large"
                                color="primary"
                                width="1-1"
                                :waiting="creating"
                                :disabled="creating"
                                class="uk-width-1-1"
                            >
                                Save
                            </base-button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
        <comments-dialog
            :show.sync="displaying_comments"
            :title="`Comments for ${main_firm.name}`"
            :comments="comments"
            :owner-id="main_firm.id || 0"
            owner-type="App.Firm"
            :editable="false"
        />
    </modal-dialog>
</template>

<script type="text/javascript">
import { mapGetters } from "vuex"

import ui_utilities from "../mixins/UiUtilities"
import string_utilities from "../mixins/StringUtilities"
import project_utilities from "../mixins/ProjectUtilities"
import has_addresses from "../mixins/HasAddresses"
import handles_errors from "../mixins/HandlesErrors"

import ModalDialog from "./ModalDialog/ModalDialog"
import FormInput from "../ui/FormInput"
import FormDateInput from "../ui/FormDateInput"
import FormSelectCombo from "../ui/FormSelectCombo"
import FormSelectUser from "../ui/FormSelectUser"
import FirmPicker from "../ui/FirmPicker"
import BaseButton from "../ui/BaseButton"
import OpenIcon from "../ui/OpenIcon"
import CommentsDialog from "../dialogs/CommentsDialog"

export default {
    mixins: [ui_utilities, string_utilities, project_utilities, has_addresses, handles_errors],
    components: {
        ModalDialog,
        FormInput,
        FormDateInput,
        FormSelectCombo,
        FormSelectUser,
        FirmPicker,
        BaseButton,
        OpenIcon,
        CommentsDialog
    },
    mounted () {
        this.reset_data()
    },
    watch: {
        show: "maybe_reset_data"
    },
    methods: {
        reset_data () {
            this.$store.dispatch("FETCH_RECORDS", { entity: "user" })
            this.project.internal = false
            this.project.name = ""
            this.project.nickname = null
            this.project.studio_ids = [1]
            this.project.firm_id = 0
            this.project.firm_name = null
            this.project.contact_info = {
                firm_id: 0,
                firm: null,
                first_name: null,
                last_name: null,
                email: null
            }
            this.project.role = 0
            this.project.category_ids = []
            this.project.type_of_request = null
            this.project.size = 0
            this.project.size_units = "SF"
            this.project.construction_budget = 0
            this.project.electrical_budget = 0
            this.project.lighting_budget = 0
            this.project.controls_budget = 0
            this.project.pic_id = 0
            this.project.pm_id = 0
            this.project.received_by = this.profile_id
            this.project.received_at = this.$moment().format("YYYY-MM-DD")
            this.project.duplicate_from = 0
            this.project.address.line1 = null
            this.project.address.city = null
            this.project.address.state = null
            this.project.address.country = null
            this.project.address.zip = null
            this.project.address.latitude = 0
            this.project.address.longitude = 0
            this.project.projected_awarded_at = null
            this.project.project_group_id = null
            this.project.group_id = null
            this.main_firm = {}

            if(this.sourceData) {
                this.project.name = this.sourceData.name
                this.project.studio_ids = this.sourceData.studio_ids
                this.project.type_of_request = this.sourceData.type_of_request
                this.project.pic_id = this.sourceData.pic_id
                this.project.pm_id = this.sourceData.pm_id
                this.project.duplicate_from = this.sourceData.id

                if(!this.scope) {
                    this.project.category_ids = this.sourceData.category_ids.slice()
                    this.project.size = this.sourceData.size
                    this.project.size_units = this.sourceData.size_units
                    this.project.construction_budget = this.sourceData.construction_budget
                    this.project.electrical_budget = this.sourceData.electrical_budget
                    this.project.lighting_budget = this.sourceData.lighting_budget
                    this.project.controls_budget = this.sourceData.controls_budget
                    this.project.projected_awarded_at = this.sourceData.projected_awarded_at ? this.$moment(this.sourceData.projected_awarded_at).format("YYYY-MM-DD") : null
                    if(this.sourceData.group_id) {
                        this.project.project_group_id = this.sourceData.project_group_id
                        this.project.group_id = this.sourceData.group_id
                    }
                } else {
                    this.project.name = this.sourceData.name
                    this.project.activation_scope_number = this.sourceData.number
                    this.$store.dispatch(
                        "FETCH_RECORDS",
                        {
                            entity: "address",
                            query: { project_id: this.sourceData.id }
                        }
                    ).then(() => {
                        const address = this.physical_address("App\\Project", this.sourceData.id)
                        if(address) {
                            this.project.address.line1 = address.line1
                            this.project.address.city = address.city
                            this.project.address.state = address.state
                            this.project.address.country = address.country
                            this.project.address.zip = address.zip
                            this.project.address.latitude = address.latitude
                            this.project.address.longitude = address.longitude
                        }
                    }).catch(error => {
                        this.$error(error.message)
                    })
                    this.$store.dispatch(
                        "FETCH_RECORDS",
                        {
                            entity: "contact_project",
                            query: { project_id: this.sourceData.id, relation_ids: ["contact", "firm"] }
                        }
                    ).then(() => {
                        const main_contact_project = this.entity_records("contact_project").find(contact_project => (contact_project.internal_category === "main") && (contact_project.project_id === this.sourceData.id))
                        if(main_contact_project) {
                            this.project.firm_id = main_contact_project.firm_id
                            this.main_firm = this.entity_records("firm").find(firm => (firm.id === main_contact_project.firm_id))
                            this.project.firm_name = this.main_firm ? this.main_firm.name : ""
                            this.project.contact_info = this.entity_records("contact").find(contact => (contact.id === main_contact_project.contact_id)) || null
                            this.project.role = main_contact_project.role
                        }
                    }).catch(error => {
                        this.$error(error.message)
                    })
                }
            }
        },
        maybe_reset_data () {
            if(this.show) this.reset_data()
        },
        set_firm (firm) {
            this.main_firm = firm
            if(firm.id) {
                this.$store.dispatch(
                    "FETCH_RECORDS",
                    {
                        entity: "comment",
                        query: { firm_id: firm.id }
                    }
                )
            }
            this.project.firm_id = firm.id || 0
            this.project.firm_name = firm.name || ""
            this.project.contact_info.firm_id = this.project.firm_id
            this.project.contact_info.firm_name = this.project.firm_name
        },
        create () {
            this.clear_all_errors()
            this.creating = true
            this.project.internal = this.internal

            this.$store.dispatch("CREATE_RECORD", {
                entity: "project",
                data: this.project
            }).then(record => {
                if(record.id) {
                    this.$router.push({
                        name: this.internal ? "project.edit" : "prospective_project.edit",
                        params: { id: record.id }
                    })
                    this.close()
                }
            }).catch(this.receive_error).then(() => {
                this.creating = false
            })
        },
        close () {
            this.$emit("update:show", false)
        }
    },
    computed: {
        ...mapGetters(["entity_records", "entity_record"]),
        shown: {
            get: function () {
                return this.show
            },
            set: function (show) {
                if(!show) this.close()
            }
        },
        comments () {
            if(!this.main_firm.id) return []
            return this.entity_records("comment").filter(comment => {
                return (comment.topic_type === "App\\Firm") && (comment.topic_id === this.main_firm.id)
            })
        }
    },
    props: {
        show: {
            type: Boolean,
            default: false
        },
        internal: {
            type: Boolean,
            default: false
        },
        sourceData: {
            validator: prop => !prop || (typeof prop === "object"),
            default () {
                return null
            }
        },
        scope: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            creating: false,
            displaying_comments: false,
            main_firm: {},
            project: {
                internal: false,
                name: "",
                nickname: null,
                studio_ids: [1],
                firm_id: 0,
                firm_name: null,
                contact_info: {
                    firm_id: 0,
                    firm: null,
                    first_name: null,
                    last_name: null,
                    email: null
                },
                role: 0,
                category_ids: [],
                type_of_request: null,
                size: 0,
                size_units: "SF",
                construction_budget: 0,
                electrical_budget: 0,
                lighting_budget: 0,
                controls_budget: 0,
                pic_id: 0,
                pm_id: 0,
                received_by: 0,
                requested_at: null,
                duplicate_from: 0,
                activation_scope_number: null,
                address: {
                    line1: null,
                    city: null,
                    state: null,
                    country: null,
                    zip: null,
                    latitude: 0,
                    longitude: 0
                },
                projected_awarded_at: null,
                project_group_id: null,
                group_id: null
            }
        }
    }
}
</script>
